export const GA_MEASUREMENT_ID = 'G-N0S7JDQJJX';

export type RouteAnalytics = {
  screenName: string;
  routeTemplate: string;
  extraParams?: Record<string, string>;
};

/** @deprecated Use RouteAnalytics */
export type PatientsRouteAnalytics = RouteAnalytics;

type RouteDefinition = {
  pattern: RegExp;
  screenName: string;
  routeTemplate: string;
  getExtraParams?: (pathname: string) => Record<string, string>;
};

function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag(...args);
  }
}

export function trackScreenView(
  screenName: string,
  screenClass: string,
  params: Record<string, string> = {},
) {
  const pagePath = params.page_path ?? params.route_template;
  const eventParams: Record<string, string> = {
    screen_name: screenName,
    screen_class: screenClass,
    page_title: screenName,
    ...params,
  };

  if (pagePath) {
    eventParams.page_path = pagePath;
  }

  gtag('event', 'screen_view', eventParams);

  // Manual page_view for GA4 path reports (auto page_view is disabled in index.html).
  // Uses sanitized route_template only — never raw URLs with patient/estimate IDs.
  if (pagePath) {
    gtag('event', 'page_view', {
      page_title: screenName,
      page_path: pagePath,
    });
  }
}

function getRouteAnalytics(
  pathname: string,
  prefix: string,
  routes: RouteDefinition[],
  fallback: RouteAnalytics,
): RouteAnalytics | null {
  if (!pathname.startsWith(prefix)) {
    return null;
  }

  for (const route of routes) {
    if (route.pattern.test(pathname)) {
      return {
        screenName: route.screenName,
        routeTemplate: route.routeTemplate,
        extraParams: route.getExtraParams?.(pathname),
      };
    }
  }

  return fallback;
}

function trackAreaRouteScreenView(
  pathname: string,
  appArea: string,
  screenClass: string,
  getAnalytics: (pathname: string) => RouteAnalytics | null,
) {
  const route = getAnalytics(pathname);
  if (!route) {
    return;
  }

  trackScreenView(route.screenName, screenClass, {
    app_area: appArea,
    route_template: route.routeTemplate,
    ...(route.extraParams ?? {}),
  });
}

const PATIENTS_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/patients\/[^/]+\/estimate\/[^/]+\/risk\/?$/,
    screenName: 'Risk',
    routeTemplate: '/patients/:patientId/estimate/:estimateId/risk',
  },
  {
    pattern: /^\/patients\/[^/]+\/estimate\/[^/]+\/?$/,
    screenName: 'Estimate Detail',
    routeTemplate: '/patients/:patientId/estimate/:estimateId',
  },
  {
    pattern: /^\/patients\/[^/]+\/estimate\/?$/,
    screenName: 'Estimate',
    routeTemplate: '/patients/:patientId/estimate',
  },
  {
    pattern: /^\/patients\/[^/]+\/vob\/[^/]+\/?$/,
    screenName: 'VOB Detail',
    routeTemplate: '/patients/:patientId/vob/:vobId',
  },
  {
    pattern: /^\/patients\/[^/]+\/vob\/?$/,
    screenName: 'VOB',
    routeTemplate: '/patients/:patientId/vob',
  },
  {
    pattern: /^\/patients\/[^/]+\/?$/,
    screenName: 'Patient',
    routeTemplate: '/patients/:patientId',
  },
  {
    pattern: /^\/patients\/?$/,
    screenName: 'Patient Search',
    routeTemplate: '/patients',
  },
];

const HUB_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/pay\/change-recurring\/?$/,
    screenName: 'Change Recurring PM',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId/pay/change-recurring',
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/pay\/?$/,
    screenName: 'Take Payment',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId/pay',
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/program\/custom-results\/?$/,
    screenName: 'Program Custom Results',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId/program/custom-results',
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/program\/[^/]+\/?$/,
    screenName: 'Program Setup',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId/program/:programType',
    getExtraParams: (pathname): Record<string, string> => {
      const match = pathname.match(/\/program\/([^/]+)\/?$/);
      return match ? { program_type: match[1] } : {};
    },
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/program\/?$/,
    screenName: 'Program Setup',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId/program',
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/ioc\/[^/]+\/?$/,
    screenName: 'IOC Summary',
    routeTemplate: '/hub/patient/:patientId/ioc/:patientEncounterId',
  },
  {
    pattern: /^\/hub\/patient\/[^/]+\/?$/,
    screenName: 'Patient Select IOC',
    routeTemplate: '/hub/patient/:patientId',
  },
  {
    pattern: /^\/hub\/patient\/?$/,
    screenName: 'New Patient Search',
    routeTemplate: '/hub/patient',
  },
  {
    pattern: /^\/hub\/?$/,
    screenName: 'Hub Home',
    routeTemplate: '/hub',
  },
];

const MOBILE_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/mobile\/patient\/[^/]+\/ioc\/[^/]+\/no-payment-programs\/?$/,
    screenName: 'No Payment Programs',
    routeTemplate: '/mobile/patient/:patientId/ioc/:patientEncounterId/no-payment-programs',
  },
  {
    pattern: /^\/mobile\/patient\/[^/]+\/ioc\/[^/]+\/pay\/?$/,
    screenName: 'Take Payment',
    routeTemplate: '/mobile/patient/:patientId/ioc/:patientEncounterId/pay',
  },
  {
    pattern: /^\/mobile\/patient\/[^/]+\/ioc\/[^/]+\/program\/custom-results\/?$/,
    screenName: 'Program Custom Results',
    routeTemplate: '/mobile/patient/:patientId/ioc/:patientEncounterId/program/custom-results',
  },
  {
    pattern: /^\/mobile\/patient\/[^/]+\/ioc\/[^/]+\/program\/[^/]+\/?$/,
    screenName: 'Program Setup',
    routeTemplate: '/mobile/patient/:patientId/ioc/:patientEncounterId/program/:programType',
    getExtraParams: (pathname): Record<string, string> => {
      const match = pathname.match(/\/program\/([^/]+)\/?$/);
      return match ? { program_type: match[1] } : {};
    },
  },
  {
    pattern: /^\/mobile\/patient\/[^/]+\/ioc\/[^/]+\/program\/?$/,
    screenName: 'Program Setup',
    routeTemplate: '/mobile/patient/:patientId/ioc/:patientEncounterId/program',
  },
];

const SPECIALIST_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/specialist\/dashboard\/dispute\/[^/]+\/?$/,
    screenName: 'Dispute Response',
    routeTemplate: '/specialist/dashboard/dispute/:disputeId',
  },
  {
    pattern: /^\/specialist\/dashboard\/client\/[^/]+\/patient\/[^/]+\/ledger\/?$/,
    screenName: 'Master Ledger',
    routeTemplate: '/specialist/dashboard/client/:clientId/patient/:patientId/ledger',
  },
  {
    pattern: /^\/specialist\/dashboard\/[^/]+\/?$/,
    screenName: 'Patient Details',
    routeTemplate: '/specialist/dashboard/:patientId',
  },
  {
    pattern: /^\/specialist\/dashboard\/?$/,
    screenName: 'Patient Records Dashboard',
    routeTemplate: '/specialist/dashboard',
  },
];

const ESTIMATE_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/estimate\/v4\/[^/]+\/?$/,
    screenName: 'Estimate V4 Detail',
    routeTemplate: '/estimate/v4/:estimateId',
  },
  {
    pattern: /^\/estimate\/v3\/[^/]+\/?$/,
    screenName: 'Estimate V3 Detail',
    routeTemplate: '/estimate/v3/:estimateId',
  },
  {
    pattern: /^\/estimate\/[^/]+\/?$/,
    screenName: 'Estimate Detail',
    routeTemplate: '/estimate/:estimateId',
  },
];

const ADMISSIONS_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/admissions-advisor\/inbound\/?$/,
    screenName: 'Admissions Inbound',
    routeTemplate: '/admissions-advisor/inbound',
  },
];

const CRM_ROUTE_DEFINITIONS: RouteDefinition[] = [
  {
    pattern: /^\/crm\/?$/,
    screenName: 'CRM Integration',
    routeTemplate: '/crm',
  },
];

export function getPatientsRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/patients', PATIENTS_ROUTE_DEFINITIONS, {
    screenName: 'Patients',
    routeTemplate: '/patients/*',
  });
}

export function getHubRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/hub', HUB_ROUTE_DEFINITIONS, {
    screenName: 'Payment Hub',
    routeTemplate: '/hub/*',
  });
}

export function getMobileRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/mobile', MOBILE_ROUTE_DEFINITIONS, {
    screenName: 'Payment Hub Mobile',
    routeTemplate: '/mobile/*',
  });
}

export function getSpecialistRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/specialist', SPECIALIST_ROUTE_DEFINITIONS, {
    screenName: 'Patient Records',
    routeTemplate: '/specialist/*',
  });
}

export function getEstimateRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/estimate', ESTIMATE_ROUTE_DEFINITIONS, {
    screenName: 'Estimate',
    routeTemplate: '/estimate/*',
  });
}

export function getAdmissionsAdvisorRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/admissions-advisor', ADMISSIONS_ROUTE_DEFINITIONS, {
    screenName: 'Admissions Advisor',
    routeTemplate: '/admissions-advisor/*',
  });
}

export function getCrmRouteAnalytics(pathname: string): RouteAnalytics | null {
  return getRouteAnalytics(pathname, '/crm', CRM_ROUTE_DEFINITIONS, {
    screenName: 'CRM',
    routeTemplate: '/crm/*',
  });
}

export function trackPatientsRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'patients', 'EstimatesView', getPatientsRouteAnalytics);
}

export function trackHubRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'payment_hub', 'PaymentHub', getHubRouteAnalytics);
}

export function trackMobileRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'payment_hub_mobile', 'PaymentHubMobile', getMobileRouteAnalytics);
}

export function trackSpecialistRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'specialist', 'PatientRecords', getSpecialistRouteAnalytics);
}

export function trackEstimateRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'estimate', 'EstimateView', getEstimateRouteAnalytics);
}

export function trackAdmissionsAdvisorRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'admissions_advisor', 'AdmissionsAdvisor', getAdmissionsAdvisorRouteAnalytics);
}

export function trackCrmRouteScreenView(pathname: string) {
  trackAreaRouteScreenView(pathname, 'crm', 'CrmIntegration', getCrmRouteAnalytics);
}

export function getRouteTemplateForTrackedPath(pathname: string): string | undefined {
  return (
    getPatientsRouteAnalytics(pathname)?.routeTemplate ??
    getHubRouteAnalytics(pathname)?.routeTemplate ??
    getMobileRouteAnalytics(pathname)?.routeTemplate ??
    getSpecialistRouteAnalytics(pathname)?.routeTemplate ??
    getEstimateRouteAnalytics(pathname)?.routeTemplate
  );
}

export function trackClick(action: string, params: Record<string, string> = {}) {
  gtag('event', 'button_click', {
    action,
    ...params,
  });
}

/** User-scoped GA4 property — register as a User custom dimension in GA Admin. */
export const FINPAY_USER_ID_USER_PROPERTY = 'finpay_user_id';

function getCurrentPagePath(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.location.pathname;
}

/** Sets GA user_id (identity) and finpay_user_id (segmentable user property). */
export function setAnalyticsUserIdentity(userId: string) {
  gtag('set', { user_id: userId });
  gtag('set', 'user_properties', {
    [FINPAY_USER_ID_USER_PROPERTY]: userId,
  });

  const pagePath = getCurrentPagePath();
  gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
    ...(pagePath ? { page_path: pagePath } : {}),
  });
}

/** Clears GA user identity on logout. */
export function clearAnalyticsUserIdentity() {
  gtag('set', { user_id: null });
  gtag('set', 'user_properties', {
    [FINPAY_USER_ID_USER_PROPERTY]: null,
  });

  const pagePath = getCurrentPagePath();
  gtag('config', GA_MEASUREMENT_ID, {
    user_id: null,
    ...(pagePath ? { page_path: pagePath } : {}),
  });
}
